import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { InqueritoService } from '../inquerito/inquerito.service';
import { Inquerito } from '../inquerito/inquerito.model';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class CountdownService {

  private intervalId: any;
  private serverCheckInterval: any;
  private destroy$ = new Subject<void>();

  private remainingTimeSubject = new BehaviorSubject<string>('');
  private prefixMessageSubject = new BehaviorSubject<string>('');
  private statusSubject = new BehaviorSubject<string>('');

  remainingTime$: Observable<string> = this.remainingTimeSubject.asObservable();
  prefixMessage$: Observable<string> = this.prefixMessageSubject.asObservable();
  status$: Observable<string> = this.statusSubject.asObservable();

  private surveyStartingText: string = '';
  private surveyEndingText: string = '';
  private surveyEndText: string = '';
  private timeUpText: string = '';

  constructor(private inquiryService: InqueritoService, private translateService: TranslateService) {
    this.init();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearIntervals();
  }

  private init(): void {
    this.translateService.get([
      'survey_starting',
      'survey_ending',
      'survey_end',
      'time_up'
    ]).subscribe(translations => {
      this.surveyStartingText = translations['survey_starting'];
      this.surveyEndingText = translations['survey_ending'];
      this.surveyEndText = translations['survey_end'];
      this.timeUpText = translations['time_up'];

      const cachedInquerito = this.getCachedInquerito();
      if (cachedInquerito) {
        this.startCountdownWithValidation(cachedInquerito);
      }
      this.loadInqueritoFromServer();
      this.serverCheckInterval = setInterval(() => this.loadInqueritoFromServer(), 1000);
    });
  }

  private clearIntervals(): void {
    clearInterval(this.intervalId);
    clearInterval(this.serverCheckInterval);
  }

  private loadInqueritoFromServer(): void {
    this.inquiryService.getInquiryMdC().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (inquerito: Inquerito) => {
        if (inquerito) {
          this.cacheInquerito(inquerito);
          this.startCountdownWithValidation(inquerito);
        }
      },
      error: (err) => {
        console.error('Falha ao buscar inquérito do servidor, usando cache local', err);
      }
    });
  }

  private startCountdownWithValidation(inquerito: Inquerito): void {
    const now = new Date(inquerito.hoje);
    const startDate = new Date(inquerito.dataInicio);
    const endDate = new Date(inquerito.dataTermino);

    if (now < startDate) {
      this.statusSubject.next('pending');
      this.startCountdown(now, startDate, this.surveyStartingText);
    } else if (now < endDate) {
      this.statusSubject.next('running');
      this.startCountdown(now, endDate, this.surveyEndingText);
    } else {
      this.statusSubject.next('finished');
      this.remainingTimeSubject.next(this.surveyEndText);
      this.prefixMessageSubject.next('');
    }
  }

  private startCountdown(now: Date, targetDate: Date, prefixMessage: string = ''): void {
    clearInterval(this.intervalId);
    this.prefixMessageSubject.next(prefixMessage);
    this.updateRemainingDays(now, targetDate);
    this.intervalId = setInterval(() => this.updateRemainingDays(now, targetDate), 1000);
  }

  private updateRemainingDays(now: Date, targetDate: Date): void {
    const timeDiff = targetDate.getTime() - now.getTime();
    const prefixMessage = this.prefixMessageSubject.getValue();

    if (timeDiff <= 0) {
      this.remainingTimeSubject.next(prefixMessage ? "00 : 00 : 00 : 00" : this.timeUpText);
      clearInterval(this.intervalId);

      if (prefixMessage === this.surveyStartingText) {
        const cachedInquerito = this.getCachedInquerito();
        if (cachedInquerito) {
          this.startCountdownWithValidation(cachedInquerito);
        }
      }
      return;
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    this.remainingTimeSubject.next(
      `${days} : ${hours.toString().padStart(2, '0')} : ${minutes.toString().padStart(2, '0')} : ${seconds.toString().padStart(2, '0')}`
    );
  }

  private cacheInquerito(inquerito: Inquerito): void {
    const hoje = new Date(inquerito.hoje);
    const dataInicio = new Date(inquerito.dataInicio);
    const dataTermino = new Date(inquerito.dataTermino);

    localStorage.setItem('cachedInquerito', JSON.stringify({
      ...inquerito,
      hoje: hoje.toISOString(),
      dataInicio: dataInicio.toISOString(),
      dataTermino: dataTermino.toISOString()
    }));
  }

  private getCachedInquerito(): Inquerito | null {
    const cached = localStorage.getItem('cachedInquerito');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        return {
          ...parsed,
          hoje: new Date(parsed.hoje),
          dataInicio: new Date(parsed.dataInicio),
          dataTermino: new Date(parsed.dataTermino)
        };
      } catch (e) {
        console.error('Erro ao parsear inquérito do cache', e);
        return null;
      }
    }
    return null;
  }

}
